import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import { type ApplicationConfig, inject, provideZoneChangeDetection } from '@angular/core';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideMissingTranslationHandler, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ENVIRONMENT_SETTINGS } from '@reconcept-platform/env';
import { provideFeatureToggle, setLanguageGuard } from '@reconcept-platform/shared/app/core';
import {
  AUTH_TOKEN_SERVICE,
  getGraphqlUrlFactory,
  provideAuthTokensFromEnv,
  WebAuthTokenService,
} from '@reconcept-platform/shared/authentication';
import { ApolloGraphqlConfigService, introspectionQueryResultData } from '@reconcept-platform/shared/data-access';
import { LocaleProvider, RcpMissingTranslationHandlerService } from '@reconcept-platform/shared/util';
import { provideNamedApollo } from 'apollo-angular';

registerLocaleData(localeNl);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      [
        {
          path: '',
          canActivate: [setLanguageGuard],
          loadChildren: () =>
            import('@reconcept-platform/login/feature-login').then((m) => m.sharedAuthFeatureLoginAppRoutes),
        },
      ],
      withViewTransitions(),
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    { provide: AUTH_TOKEN_SERVICE, useClass: WebAuthTokenService },
    ...provideAuthTokensFromEnv(),
    LocaleProvider,
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
      missingTranslationHandler: provideMissingTranslationHandler(RcpMissingTranslationHandlerService),
    }),
    provideNamedApollo(() => {
      const environmentSettings = inject(ENVIRONMENT_SETTINGS);
      const generalConfig = inject(ApolloGraphqlConfigService);

      return {
        [environmentSettings.portfolioClientName]: {
          ...generalConfig.create(
            environmentSettings.portfolioClientName,
            getGraphqlUrlFactory(),
            introspectionQueryResultData,
          ),
        },
      };
    }),
    // The providers here are initialized in the order they appear in this list, which is why order matters here,
    // as this provider requires the graphQlService.
    provideFeatureToggle(),
  ],
};
